// ============================================================================
// TOP CLIENTS. Concentration risk: how much of the book sits with how few.
// ----------------------------------------------------------------------------
// Ranked by summed ex-GST value, never by job count. One $200k job is a bigger
// dependency than four $5k ones, and a count ranking would hide exactly the
// client whose departure would hurt.
//
// Rows with no client name are excluded from the ranking, from the count and
// from the stamp's census, which is why the stamp says "n=94 named" rather than
// the window's full n. Averaging unnamed jobs into a client ranking would state
// something false about who the work is with, and it would do it silently.
// ============================================================================

import { censusPhrase } from '../model';
import type {
    AnalysisContext,
    Cell,
    PanelDef,
    PanelReadiness,
    PanelSeries,
} from '../registry';
import { money, moneyCompact } from '../model';
import type { DerivedRow } from '../model';

/** Below this, the ranking describes anecdotes rather than concentration. */
const MIN_NAMED_FOR_LIVE = 5;

interface ClientGroup {
    key: string;
    label: string;
    count: number;
    value: number;
}

/**
 * Grouping key. Case and internal whitespace are noise from hand-typed intake,
 * so "RJ  Bird" and "rj bird" are one client; anything more aggressive would
 * start merging genuinely different companies.
 */
function nameKey(client: string): string {
    return client.replace(/\s+/g, ' ').trim().toLowerCase();
}

function displayName(client: string): string {
    return client.replace(/\s+/g, ' ').trim();
}

function namedRows(rows: DerivedRow[]): DerivedRow[] {
    return rows.filter(r => nameKey(r.client) !== '');
}

function sumValue(rows: DerivedRow[]): number {
    return rows.reduce((s, r) => s + r.value, 0);
}

/** Deterministic order: value, then count, then label, so print and screen agree. */
function groupByClient(rows: DerivedRow[]): ClientGroup[] {
    const byKey = new Map<string, ClientGroup>();
    for (const r of rows) {
        const key = nameKey(r.client);
        if (!key) continue;
        const existing = byKey.get(key);
        if (existing) {
            existing.count += 1;
            existing.value += r.value;
        } else {
            byKey.set(key, { key, label: displayName(r.client), count: 1, value: r.value });
        }
    }
    return [...byKey.values()].sort(
        (a, b) => b.value - a.value || b.count - a.count || a.label.localeCompare(b.label, 'en-AU'),
    );
}

function clamp01(n: number): number {
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(1, n));
}

function esc(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/** SVG has no text-overflow, so the budget is computed before serialising. */
function fit(label: string, widthPx: number, fontPx: number): string {
    const budget = Math.max(3, Math.floor(widthPx / (fontPx * 0.58)));
    if (label.length <= budget) return label;
    return label.slice(0, Math.max(1, budget - 1)) + '…';
}

const EMPTY_SERIES = 'No job in this window carries a client name.';

const topClients: PanelDef = {
    id: 'top-clients',
    title: 'Top clients',
    hint: 'top 6 by quoted value',
    group: 'money',
    defaultSpan: 6,
    minSpan: 6,
    slim: false,
    mark: 'bars',
    orientation: 'row',
    respectsPeriod: true,

    compute(ctx: AnalysisContext): PanelSeries {
        const named = namedRows(ctx.rows);
        const namedValue = sumValue(named);
        // EVERY ranked client becomes a cell. The six-row cap is a CHART
        // decision and belongs to the shell, which knows the panel's span; a
        // panel that truncates at compute time also truncates the CSV and the
        // table view, so an operator exporting "Top clients" would silently
        // receive six of forty and have no way to tell.
        const top = groupByClient(ctx.rows);
        // Weight is a share of the LEAD client, so the top bar always fills the
        // track and the rest read as "how close is anyone to the biggest".
        const leadValue = top[0]?.value ?? 0;

        const cells: Cell[] = top.map(g => ({
            key: `client:${g.key}`,
            label: g.label,
            count: g.count,
            value: g.value,
            weight: leadValue > 0 ? clamp01(g.value / leadValue) : 0,
            select(rows: DerivedRow[]): DerivedRow[] {
                return rows.filter(r => nameKey(r.client) === g.key);
            },
            formula(picked: DerivedRow[]): string {
                const sum = sumValue(picked);
                const share = namedValue > 0 ? Math.round((sum / namedValue) * 100) : 0;
                return `${g.label} · ${picked.length} rows × sum(scope[].amount) ex GST = ${money(sum)}, ${share}% of the named book`;
            },
            drill: {
                // A client name is a precise term. Fuzzy matching would pull in
                // every job whose address or scope happens to share a word.
                lane: 'all',
                query: g.label,
                sort: 'value',
                // NOT exact. Two reasons, both verified: matchesRunSheetSearch
                // matches an unanchored substring across a haystack that includes
                // address and scope wording, so a single-token client name pulls
                // in unrelated jobs; and this panel respects the period while the
                // Run Sheet handoff carries none, so the destination is a superset
                // whenever a client has older work. trade-mix reached the same
                // answer; claiming exactness we cannot deliver is worse than
                // letting the reconcile line state the difference.
                exact: false,
            },
        }));

        // `total` is the panel's POPULATION (every NAMED row), not the sum of the
        // marks: only the top clients get a mark, so summing the marks would
        // under-report the book. The stamp quotes this number.
        return {
            cells,
            total: { count: named.length, value: namedValue },
            emptySeries: EMPTY_SERIES,
        };
    },

    readiness(_s: PanelSeries, ctx: AnalysisContext): PanelReadiness {
        // An empty window is a shell empty state, not dormancy. See intake-volume.
        if (ctx.rows.length === 0) return 'live';
        const named = namedRows(ctx.rows).length;
        // Rows exist but NONE carry a client: that is a genuine source gap.
        if (named === 0) return 'dormant';
        const unnamed = ctx.rows.length - named;
        if (named < MIN_NAMED_FOR_LIVE || unnamed > named) return 'thin';
        return 'live';
    },

    activation(_s: PanelSeries, ctx: AnalysisContext): string | null {
        const named = namedRows(ctx.rows).length;
        const total = ctx.rows.length;
        const unnamed = total - named;
        if (named === 0) {
            return 'No job in this window carries a client name, so there is nothing to rank. Fill in the client company or contact name on a job and it appears here.';
        }
        if (named < MIN_NAMED_FOR_LIVE) {
            return `Only ${named} of ${total} jobs carry a client name, which is too few to describe concentration. Naming the client on more jobs fills this in.`;
        }
        if (unnamed > named) {
            return `${unnamed} of ${total} jobs carry no client name, so this ranks a minority of the book. Naming the client on a job adds it to the ranking.`;
        }
        return null;
    },

    // The stamp counts NAMED rows only. See PanelDef.population.
    population: namedRows,

    stamp(_s: PanelSeries, ctx: AnalysisContext): string {
        const named = namedRows(ctx.rows).length;
        return `${censusPhrase(named, ctx.window.capped, 'named')} · formData.clientCompany · scope[].amount ex GST`;
    },

    fields: ['formData.clientCompany', 'formData.clientName', 'scope[].amount', 'scopeGstInclusive'],

    caveats: ['Jobs that carry no client name are not counted here.'],

    csv(s: PanelSeries): { headers: string[]; rows: Record<string, string | number>[] } {
        return {
            headers: ['Client', 'Jobs', 'Value ex GST'],
            rows: s.cells.map(c => ({
                Client: c.label,
                Jobs: c.count,
                'Value ex GST': Math.round(c.value),
            })),
        };
    },

    printSvg(s: PanelSeries, w: number, h: number): string {
        const open = (label: string) =>
            `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" role="img" aria-label="${esc(label)}">`;

        if (s.cells.length === 0) {
            return (
                open(EMPTY_SERIES) +
                `<text x="0" y="${Math.round(h / 2)}" font-size="9" fill="currentColor" opacity="0.7">${esc(EMPTY_SERIES)}</text>` +
                '</svg>'
            );
        }

        // Track geometry mirrors --an-hbar-cols so paper and screen read alike.
        const gapY = 4;
        const gapX = 8;
        const countW = 26;
        const valueW = 58;
        const labelW = Math.min(130, Math.max(64, Math.round(w * 0.28)));
        const trackW = Math.max(8, w - labelW - countW - valueW - gapX * 3);
        const trackX = labelW + gapX;
        const countX = trackX + trackW + gapX + countW;
        const valueX = w;

        const n = s.cells.length;
        const rowH = Math.max(9, Math.floor((h - gapY * (n - 1)) / n));
        const labelPx = 8.5;
        const barH = Math.max(4, Math.min(12, rowH - 4));

        const top3 = s.cells
            .slice(0, 3)
            .map(c => `${c.label} ${moneyCompact(c.value)}`)
            .join(', ');

        const parts: string[] = [
            open(`Top clients by quoted value ex GST: ${top3}`),
        ];

        s.cells.forEach((c, i) => {
            const rowY = i * (rowH + gapY);
            const midY = rowY + rowH / 2;
            // Baseline computed here rather than leaning on dominant-baseline,
            // which the print renderer has no script to correct if it drifts.
            const textY = Math.round(midY + labelPx * 0.35);
            const barY = Math.round(midY - barH / 2);
            const fillW = Math.max(3, Math.round(trackW * clamp01(c.weight)));

            parts.push(
                `<text x="0" y="${textY}" font-size="${labelPx}" font-weight="700" fill="currentColor">${esc(fit(c.label, labelW, labelPx))}</text>`,
                `<rect x="${trackX}" y="${barY}" width="${trackW}" height="${barH}" rx="3" fill="currentColor" opacity="0.08"/>`,
                `<rect x="${trackX}" y="${barY}" width="${fillW}" height="${barH}" rx="3" fill="var(--qm-accent)"/>`,
                `<text x="${countX}" y="${textY}" font-size="${labelPx}" font-weight="800" text-anchor="end" fill="currentColor">${c.count}</text>`,
                `<text x="${valueX}" y="${textY}" font-size="8" font-weight="700" text-anchor="end" fill="currentColor" opacity="0.7">${esc(moneyCompact(c.value))}</text>`,
            );
        });

        parts.push('</svg>');
        return parts.join('');
    },
};

export default topClients;
